import { forwardRef, type ReactNode } from 'react';
import type { AvatarKey } from '@sincronia/shared';
import { avatarColor, cn } from '../../lib/ui.js';

export type CardSize = 'sm' | 'md' | 'lg';

const SIZES: Record<CardSize, string> = {
  sm: 'w-12 h-[4.5rem] text-xl rounded-xl',
  md: 'w-20 h-28 text-4xl rounded-2xl',
  // A carta principal acompanha a altura da janela: encolhe em telas baixas
  // (desktop em janela pequena) sem mudar de tamanho no celular.
  lg: 'w-[clamp(4.5rem,16vh,7rem)] h-[clamp(6.25rem,22vh,10rem)] text-[clamp(2.25rem,8vh,3.75rem)] rounded-3xl',
};

/** Face visível de uma carta: número muito legível. */
export const CardFace = forwardRef<
  HTMLDivElement,
  {
    value: number;
    size?: CardSize;
    className?: string;
    elevated?: boolean;
    flip?: boolean;
    dimmed?: boolean;
  }
>(function CardFace({ value, size = 'md', className, elevated, flip, dimmed }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        'card-face relative flex items-center justify-center font-semibold select-none',
        'ring-1 ring-white/40',
        SIZES[size],
        elevated ? 'shadow-2xl shadow-nova-600/40' : 'shadow-lg shadow-night-950/60',
        flip && 'animate-flip',
        dimmed && 'opacity-60',
        className,
      )}
    >
      <span className="tabular leading-none">{value}</span>
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-2 mx-auto h-px w-1/3 bg-night-900/15"
      />
    </div>
  );
});

/** Verso: usado nos arrastos remotos. Nunca mostra o número. */
export function CardBack({
  size = 'md',
  className,
  avatarKey,
  label,
}: {
  size?: CardSize;
  className?: string;
  avatarKey?: AvatarKey;
  label?: string;
}): ReactNode {
  const color = avatarKey ? avatarColor(avatarKey) : 'var(--color-pulse-500)';
  return (
    <div
      className={cn(
        'card-back relative flex items-center justify-center overflow-hidden select-none',
        'shadow-2xl shadow-night-950/70',
        SIZES[size],
        className,
      )}
      style={{ border: `1.5px solid ${color}` }}
    >
      <svg viewBox="0 0 64 64" className="absolute size-4/5 opacity-70" aria-hidden="true">
        <circle
          cx="32"
          cy="32"
          r="22"
          fill="none"
          stroke={color}
          strokeOpacity="0.35"
          strokeWidth="2"
        />
        <circle
          cx="32"
          cy="32"
          r="14"
          fill="none"
          stroke={color}
          strokeOpacity="0.55"
          strokeWidth="2"
        />
        <circle cx="32" cy="32" r="6" fill={color} fillOpacity="0.75" />
      </svg>
      {label ? (
        <span
          className="absolute bottom-1 max-w-full truncate px-1 text-[10px] font-medium"
          style={{ color }}
        >
          {label}
        </span>
      ) : null}
    </div>
  );
}
